import type { PermissionCode } from './permissions';

/**
 * The Setup hub: configuration and supporting screens reached from one sidebar entry (Setup) and one tab strip.
 * Shared by the sidebar (is Setup visible, is it active), the /setup and /admin redirects and the tab strip,
 * so the three never disagree. Each tab lists its pages; the tab links to the first page the user may open and
 * is hidden when they may open none. Permissions mirror the route guards of those pages.
 */
export type SetupTabKey = 'banks' | 'imports' | 'access' | 'organisation' | 'parties' | 'audit';

export type SetupPath = '/banks' | '/admin/imports' | '/admin/users' | '/admin/roles' | '/admin/organization' | '/admin/external-parties' | '/admin/audit-log';

export interface SetupPage {
  key: string;
  to: SetupPath;
  permission: PermissionCode;
}

export interface SetupTab {
  key: SetupTabKey;
  pages: readonly SetupPage[];
  /** URL prefixes that belong to this tab (detail and older alias routes included). */
  prefixes: readonly string[];
}

export const SETUP_TABS: readonly SetupTab[] = [
  { key: 'banks', pages: [{ key: 'banks', to: '/banks', permission: 'config.view' }], prefixes: ['/banks'] },
  { key: 'imports', pages: [{ key: 'imports', to: '/admin/imports', permission: 'import.run' }], prefixes: ['/admin/imports'] },
  {
    key: 'access',
    pages: [
      { key: 'users', to: '/admin/users', permission: 'admin.users' },
      { key: 'roles', to: '/admin/roles', permission: 'admin.roles' },
    ],
    prefixes: ['/admin/users', '/admin/roles'],
  },
  // Teams sit inside Organisation; /admin/teams stays as an older link to the same tab.
  { key: 'organisation', pages: [{ key: 'organization', to: '/admin/organization', permission: 'admin.users' }], prefixes: ['/admin/organization', '/admin/teams'] },
  { key: 'parties', pages: [{ key: 'parties', to: '/admin/external-parties', permission: 'admin.users' }], prefixes: ['/admin/external-parties'] },
  { key: 'audit', pages: [{ key: 'audit', to: '/admin/audit-log', permission: 'admin.audit' }], prefixes: ['/admin/audit-log'] },
];

/** Every permission that opens at least one Setup tab. */
export const SETUP_PERMISSIONS: readonly PermissionCode[] = [...new Set(SETUP_TABS.flatMap((tab) => tab.pages.map((p) => p.permission)))];

/** URL prefixes where the sidebar marks Setup as the current place. */
export const SETUP_PREFIXES = ['/setup', '/admin', '/banks'] as const;

export function isSetupPath(pathname: string) {
  return SETUP_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** The tabs a user may open, each with only the pages they may open (never empty). */
export function visibleSetupTabs(can: (p: PermissionCode) => boolean): SetupTab[] {
  return SETUP_TABS.map((tab) => ({ ...tab, pages: tab.pages.filter((p) => can(p.permission)) })).filter((tab) => tab.pages.length > 0);
}

/** First Setup page the user may open (the /setup and /admin redirects), or null when Setup is not theirs. */
export function firstSetupPath(can: (p: PermissionCode) => boolean): SetupPath | null {
  return visibleSetupTabs(can)[0]?.pages[0]?.to ?? null;
}

/** The tab a URL belongs to, if any. */
export function setupTabOf(pathname: string): SetupTabKey | null {
  return SETUP_TABS.find((tab) => tab.prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`)))?.key ?? null;
}
